import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { useMemo } from 'react';
import { declaredExperimentsField, useDeclaredExperiments } from './declaredExperiments';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery } from 'react-query';

// ListDisplayPrices accepts at most 50 domains per call.
const MAX_DOMAINS_PER_BATCH = 50;
const chunk = (items, size) => Array.from({
  length: Math.ceil(items.length / size)
}, (_, i) => items.slice(i * size, (i + 1) * size));

/**
 * Resolves display prices for already-loaded recommendations, so pricing can be
 * fetched lazily instead of inflating the `listRecommendations` call with the
 * PRICING feature (mirrors how `useMatchingLabels` resolves labels afterwards).
 */
export const useDisplayPrices = (recommendations, productType) => {
  const {
    listDisplayPrices
  } = DomainsApiClient.useQueries();
  const experimentIds = useDeclaredExperiments();
  const domains = recommendations.filter(rec => !!rec.domain).map(_ref => {
    let {
      domain,
      premium
    } = _ref;
    return {
      domain,
      premium: premium ?? false
    };
  });
  const {
    data,
    isLoading
  } = useQuery({
    queryKey: ['listDisplayPrices', domains, productType, experimentIds],
    enabled: domains.length > 0,
    staleTime: Infinity,
    cacheTime: Infinity,
    retry: false,
    refetchOnMount: false,
    queryFn: async () => {
      var _responses$;
      const responses = await Promise.all(chunk(domains, MAX_DOMAINS_PER_BATCH).map(batch => listDisplayPrices({
        domains: batch,
        ...(productType ? {
          productType: {
            domainProductType: productType
          }
        } : {}),
        ...declaredExperimentsField(experimentIds)
      })));
      return {
        displayPrices: responses.flatMap(res => res.displayPrices ?? []),
        pricesExcludeTax: (_responses$ = responses[0]) == null ? void 0 : _responses$.pricesExcludeTax
      };
    }
  });
  const pricesByDomain = useMemo(() => {
    const map = new Map();
    ((data == null ? void 0 : data.displayPrices) ?? []).forEach(_ref2 => {
      let {
        id,
        domain,
        ...price
      } = _ref2;
      const key = domain ?? id;
      if (key) {
        // display-price and recommendation share an identical DisplayPrice
        // shape, but are distinct generated ambassador types.
        map.set(key, price);
      }
    });
    return map;
  }, [data]);
  return {
    pricesByDomain,
    isLoading: isLoading && domains.length > 0,
    pricesExcludeTax: data == null ? void 0 : data.pricesExcludeTax
  };
};
//# sourceMappingURL=useDisplayPrices.js.map